import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { JournalistGallery } from '@/components/interviews/JournalistGallery';
import { JournalistForm } from '@/components/interviews/JournalistForm';
import { AIJournalistWizard } from '@/components/interviews/AIJournalistWizard';
import { Plus, Users, AlertCircle, Info, Sparkles, Building2 } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useAuth } from '@/context/auth-context';
import { useSite } from '@/contexts/SiteContext';

interface Journalist {
  id: string;
  sector_id?: number;
  sector_name?: string;
  name: string;
  title: string;
  bio?: string;
  photo_url?: string;
  style: 'professional' | 'sporty' | 'business' | 'conversational';
  voice_gender: 'male' | 'female' | 'neutral';
  reference_articles?: any[];
  sample_questions?: string[];
  is_active: boolean;
  total_interviews: number;
  average_rating?: number;
  created_at: string;
}

export default function JournalistsManagement() {
  const { user } = useAuth();
  const { selectedSite } = useSite();
  const [showForm, setShowForm] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [selectedJournalist, setSelectedJournalist] = useState<Journalist | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Get site sectors for filtering
  const siteSectorIds = selectedSite?.sector_ids || (selectedSite?.sector_id ? [selectedSite.sector_id] : undefined);
  // Super admins see all by default, content managers see filtered
  const isSuperAdmin = user?.role === 'super_admin';

  const handleCreate = () => {
    setSelectedJournalist(null);
    setShowForm(true);
  };

  const handleEdit = (journalist: Journalist) => {
    setSelectedJournalist(journalist);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedJournalist(null);
  };

  const handleFormSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleDelete = async (journalist: Journalist) => {
    if (!confirm(`Are you sure you want to delete ${journalist.name}?`)) {
      return;
    }

    setDeleteError(null);

    try {
      const response = await authFetch(`/api/journalists/${journalist.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete journalist');
      }

      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error('Error deleting journalist:', err);
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete journalist');
    }
  };

  // Content managers can create organization-exclusive journalists
  const canCreateJournalists = isSuperAdmin || user?.role === 'site_content_manager';

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">AI Journalists</h1>
          <p className="text-muted-foreground">
            {user?.role === 'site_content_manager' ? (
              <>Manage AI journalists for {selectedSite?.name || 'your organization'}</>
            ) : (
              <>Manage AI journalist personas for content interviews</>
            )}
          </p>
        </div>
        {canCreateJournalists && (
          <div className="flex gap-2">
            {isSuperAdmin && (
              <Button variant="outline" onClick={handleCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Manual Create
              </Button>
            )}
            <Button onClick={() => setShowWizard(true)}>
              <Sparkles className="w-4 h-4 mr-2" />
              {user?.role === 'site_content_manager' ? 'Create AI Journalist' : 'AI Wizard'}
            </Button>
          </div>
        )}
      </div>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="w-5 h-5" />
            About AI Journalists
          </CardTitle>
          <CardDescription>
            {user?.role === 'site_content_manager' ? (
              <>Create organization-exclusive journalists or use system journalists</>
            ) : (
              <>AI journalists are sector-specific personas that conduct expert interviews</>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm">
            Each journalist has a unique style, expertise, and interview approach. They can:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>Conduct real-time video/audio interviews with product experts</li>
            <li>Generate interview preparation materials and scripts</li>
            <li>Extract Q&A snippets for reuse in chatbots and product pages</li>
            <li>Produce SEO-optimized articles from interview transcripts</li>
          </ul>
          {user?.role === 'site_content_manager' && (
            <Alert className="mt-4">
              <Building2 className="h-4 w-4" />
              <AlertDescription>
                <strong>Organization Journalists:</strong> Create journalists exclusive to your organization.
                They will only be visible to users with access to {selectedSite?.name || 'your site'}.
                You can also use system journalists created by administrators.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Delete Error */}
      {deleteError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{deleteError}</AlertDescription>
        </Alert>
      )}

      {/* Gallery */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Journalist Gallery
          </CardTitle>
          <CardDescription>
            {user?.role === 'site_content_manager' ? (
              <>Your organization journalists + system journalists</>
            ) : (
              <>Browse, filter, and manage all AI journalists</>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <JournalistGallery
            key={refreshKey}
            onEdit={handleEdit}
            showEditButton={canCreateJournalists}
            selectable={false}
            showFilters={true}
            siteId={selectedSite?.id}
            sectorIds={siteSectorIds}
            defaultShowAll={isSuperAdmin}
          />
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <JournalistForm
        open={showForm}
        onClose={handleFormClose}
        journalist={selectedJournalist}
        onSuccess={handleFormSuccess}
      />

      {/* AI Wizard Dialog */}
      <AIJournalistWizard
        open={showWizard}
        onOpenChange={setShowWizard}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
}
